import React, { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { getStationTheme } from "@/lib/stationTheme";
import { useT } from "@/lib/i18n";

/**
 * PageHeader — section heading used at the top of every dashboard page.
 *
 * Visually tinted by the currently active station's genre so every screen
 * carries the same workspace identity as the sidebar WorkspaceCard.
 *
 *   ┌─ accent stripe (genre color) ──────────────────────────┐
 *   │ // EYEBROW (also genre-tinted)                          │
 *   │ Big bold title                                          │
 *   │ description text                                        │
 *   └─────────────────────────────────────────────────────────┘
 *
 * Tolerates legacy props (`icon`, `subtitle`, `badge`) silently — they were
 * passed by older pages and never rendered. Subtitle is treated as a fallback
 * for `description` so we keep parity with those call sites.
 */
export default function PageHeader({
  eyebrow, title, description, subtitle, action, testid,
  // Legacy / ignored: icon, badge
}) {
  const [theme, setTheme] = useState(getStationTheme(""));
  const t = useT();

  const refresh = useCallback(async () => {
    try {
      const r = await api.get("/stations/me");
      setTheme(getStationTheme(r.data?.genre || ""));
    } catch { /* unauthenticated or no station — keep default amber */ }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);
  useEffect(() => {
    const onSwitch = () => refresh();
    window.addEventListener("station-switched", onSwitch);
    return () => window.removeEventListener("station-switched", onSwitch);
  }, [refresh]);

  return (
    <div
      className="mb-8 rounded-lg border bg-bg-surface/30 px-5 py-5 flex flex-wrap items-end justify-between gap-4"
      style={theme.borderStyle}
      data-testid={testid}
    >
      {/* Title column — reserves a readable minimum width so long
         `action` bars can't squeeze the headline into a single
         word per line. When the action is too wide to fit, the
         parent `flex-wrap` moves the action to the next row. */}
      <div className="flex-1 min-w-[280px]">
        {eyebrow && (
          <div
            className="mb-2 inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.2em]"
            style={{ color: theme.hex }}
          >
            <span className="h-1.5 w-1.5 rounded-full" style={theme.dotStyle} />
            {eyebrow}
          </div>
        )}
        <h1 className="font-display text-4xl sm:text-5xl font-black tracking-tighter text-ink-primary">
          {typeof title === "string" ? t(title) : title}
        </h1>
        {(description || subtitle) && (
          <p className="mt-2 max-w-2xl text-sm text-ink-secondary">
            {typeof (description || subtitle) === "string" ? t(description || subtitle) : (description || subtitle)}
          </p>
        )}
      </div>
      {action && <div className="min-w-0 max-w-full">{action}</div>}
    </div>
  );
}
